import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDialogRef } from '@angular/material/dialog';
import { EstablishmentService } from '../../../services/establishment/establishment.service';
import { AdvanceSearchService } from '../../../services/Advance-search/advance-search.service';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';
import { FamilyMemberLookup } from '../../../common-files/family-member-lookup';
import { UsernameAvailability } from '../../../common-files/username-availability';

// add a branch staff member. identity is owned by SSO: the person is picked by
// family code + name and the SSO uuid (memUuid) is mapped from the pick; the app
// mirrors it and assigns the branch role. self-saving; closes with `true` on
// success. The backend enforces that the role is a branch role below the
// admin's weight.
@Component({
  selector: 'app-add-staff-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    LoaderComponent,
  ],
  templateUrl: './add-staff-dialog.component.html',
  styleUrls: ['./add-staff-dialog.component.scss', '../../../../common-style/ac-dialog.scss'],
})
export class AddStaffDialogComponent implements OnDestroy {
  form: FormGroup;
  isLoading: boolean = false;

  roles = [
    { key: 'ESTAB_ADMIN', label: 'Establishment Admin' },
    { key: 'ESTAB_OPERATOR', label: 'Establishment Operator' },
    { key: 'ESTAB_VOLUNTEER', label: 'Establishment Volunteer' },
    { key: 'ESTAB_VIEWER', label: 'Establishment Viewer' },
  ];

  // family-member picker (fills fullName + memUuid)
  readonly memberLookup: FamilyMemberLookup;
  // live username check (debounced — fires when the admin pauses typing)
  readonly usernameCheck: UsernameAvailability;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<AddStaffDialogComponent>,
    private establishmentService: EstablishmentService,
    private advanceSearchService: AdvanceSearchService,
    private toastService: ToastService
  ) {
    this.memberLookup = new FamilyMemberLookup(
      (fc) => this.advanceSearchService.fetchPhilDetails(fc),
      (memUuid) => this.advanceSearchService.checkMemberAvailability(memUuid)
    );
    this.usernameCheck = new UsernameAvailability((name) =>
      this.advanceSearchService.checkUsernameAvailability(name)
    );
    this.form = this.fb.group({
      userName: ['', [Validators.required, Validators.minLength(5)]],
      familyCode: ['', [Validators.required, Validators.pattern(/^\d{12}$/)]],
      // both are set by picking a name — never typed
      fullName: ['', Validators.required],
      memUuid: ['', Validators.required],
      roleKey: ['', Validators.required],
      validUntil: [''],
      mobileNumber: [''],
      emailId: [''],
      identityNumber: [''],
    });
    this.usernameCheck.attach(this.form.get('userName')!);
    this.memberLookup.attach(
      this.form.get('familyCode')!,
      (message) => this.toastService.error(message),
      () => {}
    );
  }

  ngOnDestroy(): void {
    this.usernameCheck.destroy();
    this.memberLookup.destroy();
  }

  // a changed family code invalidates the picked member
  onFamilyCodeInput(): void {
    this.memberLookup.reset();
    this.form.patchValue({ fullName: '', memUuid: '' });
  }

  // picking a name maps the SSO uuid internally and validates the person at
  // once — someone who already has an account is refused right here.
  onMemberPick(memUuid: string): void {
    this.form.patchValue({ fullName: this.memberLookup.fullNameOf(memUuid) });
    this.memberLookup.verifyPick(memUuid);
  }

  // a Volunteer is given for a limited time, so an expiry date is mandatory
  get isVolunteer(): boolean {
    return this.form.get('roleKey')?.value === 'ESTAB_VOLUNTEER';
  }

  submit(): void {
    if (this.form.invalid) {
      this.toastService.error('Username (5+ characters), family code, name and role are required.');
      return;
    }
    if (this.usernameCheck.status === 'taken') {
      this.toastService.error('This username is already taken.');
      return;
    }
    if (this.memberLookup.pickStatus === 'taken') {
      this.toastService.error('This person already has a user account.');
      return;
    }
    if (this.isVolunteer && !this.form.value.validUntil) {
      this.toastService.error('Volunteer role needs an expiry date.');
      return;
    }
    this.isLoading = true;
    const activeEst = localStorage.getItem('activeEstablishmentId');
    const payload = {
      ...this.form.value,
      validUntil: this.isVolunteer ? this.form.value.validUntil : null,
      establishmentId: activeEst ? Number(activeEst) : null,
    };
    this.establishmentService.addStaff(payload).subscribe({
      next: (res: any) => {
        this.toastService.success(res?.message || 'Staff member added.');
        this.isLoading = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to add staff member.');
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
